import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService, RefreshFailedError } from '../auth/auth.service';

function extractBearer(authHeader: string | null): string | null {
  if (!authHeader) return null;
  return authHeader.replace(/^Bearer\s+/i, '').trim() || null;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Não interceptar 401 do próprio login nem do refresh — são erros esperados
      const isLoginEndpoint = req.url.includes('/api/login') &&
        !req.url.includes('/api/login/refresh-token') &&
        req.method === 'POST' &&
        req.url.endsWith('/api/login');
      const isRefreshEndpoint = req.url.includes('/api/login/refresh-token');

      if (error.status !== 401 || isRefreshEndpoint || isLoginEndpoint) {
        return throwError(() => error);
      }

      // Renova de forma coordenada (single-flight na aba + lock entre abas).
      // Requisições paralelas que tomam 401 compartilham o mesmo refresh e,
      // ao concluir, repetem com o token novo. Se o refresh falhar, todas
      // recebem o erro — nenhuma fica pendurada.
      const failedToken = extractBearer(req.headers.get('Authorization'));

      return from(auth.refreshAccessToken(failedToken)).pipe(
        switchMap(accessToken =>
          next(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }))
        ),
        catchError(refreshError => {
          // Logout apenas em falha de autenticação (401/403) ou refresh
          // definitivamente inválido — nunca em erro de rede (status 0),
          // para não derrubar a sessão por instabilidade momentânea.
          const status = refreshError instanceof HttpErrorResponse ? refreshError.status : null;
          const isAuthFailure =
            refreshError instanceof RefreshFailedError || status === 401 || status === 403;
          if (isAuthFailure) {
            auth.logout();
          }
          return throwError(() => refreshError);
        })
      );
    })
  );
};
